import {
  AirQualityReading,
  AirQualityThresholds,
  FilterReplacementStatus,
  HVACMonitorReading,
  SensorType,
  calculateHVACStatus,
  extractReadingValueFromSensorEvent,
  getHVACStatusForNoData,
  getHVACStatusForNoSensor,
  useExternalLocationAirQuality,
  useHVACSensorsByGroup,
  useHvacFilter,
  useHvacFilterStatus,
  useSensorAlerts,
  ServiceRequestStatus,
  AlertStatus,
  POLLING_INTERVALS_MILLIS,
} from 'common';
import { useGetSensorRollupByHours } from 'common/src/hooks/sensors/useGetSensorRollupByHours';
import { HVACPeakLoadAnalysisService } from 'common/src/services/hvac/HVACPeakLoadAnalysisService';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSync } from '../../contexts/AppSyncContext';
import { Status } from '../../types/Status';
import { mapFilterReplacementStatusToStatus } from '../../utils';
import { useLatestSensorEvents } from '../sensors/useLatestSensorEvent';

interface AirQualityData {
  status: Status;
  indoorStatus: Status;
  outdoorStatus: Status;
  indoorAQI: number;
  outdoorAQI: number;
  indoorLabel: string;
  outdoorLabel: string;
  filterReplacementStatus: FilterReplacementStatus | null;
  filterStatus: string;
  replacementTime: string;
  filterHealthPercentage: number | null;
}

export const useAirQualitySensorStatus = (): AirQualityData => {
  const { t } = useTranslation();
  const [airQualityData, setAirQualityData] = useState<AirQualityData>({
    status: Status.Good,
    indoorLabel: t('air.status.good'),
    indoorStatus: Status.Good,
    outdoorStatus: Status.Good,
    indoorAQI: 0,
    outdoorAQI: 0,
    outdoorLabel: t('air.status.good'),
    filterReplacementStatus: null,
    filterStatus: '',
    replacementTime: '',
    filterHealthPercentage: null,
  });

  const { hub, selectedHubId } = useAppSync();
  
  // Refs for throttling API calls
  const lastFilterOrderStatusTime = useRef<number>(0);
  const lastFilterOrderStatusData = useRef<any>(null);

  const { event } = useLatestSensorEvents(SensorType.AIR_QUALITY);
  const eventId = useMemo(() => event?.id, [event?.id]);

  const { hvacSensorGroups } = useHVACSensorsByGroup(selectedHubId ?? undefined);
  const firstGroup = hvacSensorGroups[0];
  const hvacSensor = firstGroup?.indoorSensor;
  const { event: hvacEvent } = useLatestSensorEvents(SensorType.HVAC);
  const { sensorAlerts: hvacSensorAlerts } = useSensorAlerts(hvacSensor?.id);
  const { historicalData } = useGetSensorRollupByHours<HVACMonitorReading>(hvacSensor?.id, 24);
  const { getFilterOrderStatus } = useHvacFilter();
  const { filterStatus: hvacFilterStatus } = useHvacFilterStatus(hvacSensor?.id);

  const airQualityReading = useMemo(() => {
    if (!event) return null;
    return extractReadingValueFromSensorEvent<AirQualityReading>(event);
  }, [event?.id, event?.value, event?.values]);

  const { externalAirQuality } = useExternalLocationAirQuality(hub?.latitude, hub?.longitude);
  
  // Set up polling for filter status data
  useEffect(() => {
    if (selectedHubId && hvacSensor?.id) {
      // Fetch data immediately on first mount if we don't have cached data
      const initializeData = async () => {
        if (!lastFilterOrderStatusData.current) {
          try {
            const data = await getFilterOrderStatus(hvacSensor.id, selectedHubId);
            lastFilterOrderStatusTime.current = Date.now();
            lastFilterOrderStatusData.current = data;
            console.log('Initial load of filter order status data');
          } catch (error) {
            console.error('Error loading initial filter status:', error);
          }
        }
      };
      
      initializeData();
      
      // Set up polling interval (every 30 seconds)
      const interval = setInterval(async () => {
        try {
          const data = await getFilterOrderStatus(hvacSensor.id, selectedHubId);
          lastFilterOrderStatusTime.current = Date.now();
          lastFilterOrderStatusData.current = data;
          console.log('Updated filter order status data (polling)');
        } catch (error) {
          console.error('Error polling filter status:', error);
        }
      }, POLLING_INTERVALS_MILLIS.HVAC);

      return () => clearInterval(interval);
    }
  }, [selectedHubId, hvacSensor?.id, getFilterOrderStatus]);

  useEffect(() => {
    const loadFilterStatus = async (_percentPressure: number) => {
      if (hvacSensor?.id && selectedHubId) {
        // Throttle API calls to once per 30 seconds
        const now = Date.now();
        const timeSinceLastCall = now - lastFilterOrderStatusTime.current;
        let filterOrderStatus;
        
        // Use cached data if it's been less than 30 seconds since the last API call
        if (timeSinceLastCall < POLLING_INTERVALS_MILLIS.HVAC && lastFilterOrderStatusData.current) {
          console.log('Using cached filter order status data');
          filterOrderStatus = lastFilterOrderStatusData.current;
        } else {
          // It's been more than a minute or we don't have cached data, make a new API call
          console.log('Fetching fresh filter order status data');
          filterOrderStatus = await getFilterOrderStatus(hvacSensor.id, selectedHubId);
          
          // Update our cache and timestamp
          lastFilterOrderStatusTime.current = now;
          lastFilterOrderStatusData.current = filterOrderStatus;
        }

        // Check if filter order exists and is not closed/canceled
        const hasActiveFilterOrder =
          filterOrderStatus.hasFilterOrder &&
          filterOrderStatus.filterOrder?.status !== ServiceRequestStatus.CLOSED &&
          filterOrderStatus.filterOrder?.status !== ServiceRequestStatus.CANCELED;

        // Check if service request exists and is not closed/canceled
        const hasActiveServiceRequest =
          filterOrderStatus.hasServiceRequest &&
          filterOrderStatus.serviceRequest?.status !== ServiceRequestStatus.CLOSED &&
          filterOrderStatus.serviceRequest?.status !== ServiceRequestStatus.CANCELED;

        if (hasActiveFilterOrder) {
          return FilterReplacementStatus.FilterOrdered;
        } else if (hasActiveServiceRequest) {
          return FilterReplacementStatus.ServiceRequested;
        }
      }
      // Check specifically for HVAC_FILTER alerts that are unacknowledged (like the detail screen does)
      const hvacFilterAlerts = hvacSensorAlerts.filter((alert) => {
        const isHVACFilterAlert = alert.alertType === 'HVAC_FILTER';
        // Support both firmware statuses and actual system statuses
        const isActiveStatus =
          alert.status === AlertStatus.PENDING ||
          alert.status === AlertStatus.ACTIVE ||
          alert.status === 'new' ||
          alert.status === 'ongoing';
        const isUnacknowledged = !alert.acknowledgedAt && !alert.acknowledgedBy;

        return isHVACFilterAlert && isActiveStatus && isUnacknowledged;
      });

      if (hvacFilterAlerts.length > 0) {
        // If there are active HVAC filter alerts, show urgent alert status
        return FilterReplacementStatus.HVACFilterAlert;
      }

      // Check for acknowledged alerts (any type)
      if (hvacSensorAlerts.length > 0) {
        const lastAlert = hvacSensorAlerts[hvacSensorAlerts.length - 1];
        if (lastAlert.acknowledgedAt) {
          return null;
        }
      }
      

      return null;
    };

    const processData = async () => {
      let filterReplacementStatus: FilterReplacementStatus | null = null;
      let filterStatus = '';
      let replacementTime = '';
      let filterHealthPercentage: number | null = null;

      // Use HVAC filter status data directly
      if (hvacFilterStatus?.filterHealthPercentage !== undefined) {
        filterHealthPercentage = hvacFilterStatus.filterHealthPercentage;
      }

      if (hvacSensor && hvacEvent) {
        const hvacReading = extractReadingValueFromSensorEvent<HVACMonitorReading>(hvacEvent);
        if (hvacReading) {
          let finalPercentPressure = hvacReading.percentPressure || 0;

          try {
            if (historicalData && historicalData.length > 0) {
              const peakLoadAnalysis =
                await HVACPeakLoadAnalysisService.analyzeHVACPeakLoad(historicalData);
              if (peakLoadAnalysis && peakLoadAnalysis.filterHealthPercentage !== null) {
                finalPercentPressure = peakLoadAnalysis.filterHealthPercentage;
              }
            }
          } catch (error) {
            console.error('Error in peak load analysis:', error);
          }

          filterReplacementStatus = await loadFilterStatus(finalPercentPressure);
          const hvacStatusResult = calculateHVACStatus(finalPercentPressure);
          filterStatus = hvacStatusResult.filterStatus;
          replacementTime = hvacStatusResult.replacementTime;
        }
      } else if (!hvacSensor) {
        const noSensorResult = getHVACStatusForNoSensor();
        filterStatus = noSensorResult.filterStatus;
        replacementTime = noSensorResult.replacementTime;
      } else if (!hvacEvent) {
        const noDataResult = getHVACStatusForNoData();
        filterStatus = noDataResult.filterStatus;
        replacementTime = noDataResult.replacementTime;
      }

      if (event && airQualityReading && externalAirQuality) {
        const indoorAQI = typeof airQualityReading.iaq === 'number' ? airQualityReading.iaq : 0;
        const outdoorAQI =
          typeof externalAirQuality?.universalAqi?.aqi === 'number'
            ? externalAirQuality.universalAqi.aqi
            : 0;

        const iaqThresholds = AirQualityThresholds.iaq;

        let indoorLabel: string;
        let indoorStatus: Status;

        if (indoorAQI < 0) {
          indoorLabel = t('common.sensorFault');
          indoorStatus = Status.Error;
        } else {
          const indoorStatusRanges = [
            {
              threshold: iaqThresholds.excellent,
              labelKey: 'air.status.good',
              status: Status.Good,
            },
            {
              threshold: iaqThresholds.good,
              labelKey: 'air.status.good',
              status: Status.Good,
            },
            {
              threshold: iaqThresholds.lightlyPolluted,
              labelKey: 'air.status.moderate',
              status: Status.Poor,
            },
            {
              threshold: iaqThresholds.moderatelyPolluted,
              labelKey: 'air.status.moderate',
              status: Status.Poor,
            },
            {
              threshold: iaqThresholds.heavilyPolluted,
              labelKey: 'air.status.poor',
              status: Status.Error,
            },
            {
              threshold: iaqThresholds.severelyPolluted,
              labelKey: 'air.status.poor',
              status: Status.Error,
            },
          ];

          const indoorRange = indoorStatusRanges.find((range) => indoorAQI <= range.threshold) || {
            labelKey: 'air.status.poor',
            status: Status.Error,
          };

          indoorLabel = t(indoorRange.labelKey);
          indoorStatus = indoorRange.status;
        }

        const eaqThresholds = AirQualityThresholds.eaq;

        let outdoorStatus: Status;

        if (outdoorAQI < 0) {
          outdoorStatus = Status.Error;
        } else {
          const outdoorStatusRanges = [
            {
              threshold: eaqThresholds.good,
              status: Status.Good,
            },
            {
              threshold: eaqThresholds.moderate,
              status: Status.Poor,
            },
          ];

          const outdoorRange = outdoorStatusRanges.find(
            (range) => outdoorAQI <= range.threshold,
          ) || {
            status: Status.Error,
          };

          outdoorStatus = outdoorRange.status;
        }

        const filterWebStatus = mapFilterReplacementStatusToStatus(filterReplacementStatus);
        let status: Status = Status.Good;
        if (
          indoorStatus === Status.Error ||
          outdoorStatus === Status.Error ||
          filterWebStatus === Status.Error
        ) {
          status = Status.Error;
        } else if (
          indoorStatus === Status.Poor ||
          outdoorStatus === Status.Poor ||
          filterWebStatus === Status.Poor
        ) {
          status = Status.Poor;
        }

        const outdoorLabel =
          outdoorStatus === Status.Error
            ? t('devices.status.error')
            : t('devices.airQualityValue', {
                status: t(`devices.status.${outdoorStatus}`),
                value: outdoorAQI,
              });

        setAirQualityData({
          status,
          indoorStatus,
          indoorLabel,
          outdoorStatus,
          outdoorLabel,
          indoorAQI,
          outdoorAQI,
          filterReplacementStatus,
          filterStatus,
          replacementTime,
          filterHealthPercentage,
        });
      } else {
        setAirQualityData({
          status: Status.Good,
          indoorLabel: t('air.status.good'),
          indoorStatus: Status.Good,
          outdoorStatus: Status.Good,
          outdoorLabel: t('air.status.good'),
          indoorAQI: 0,
          outdoorAQI: 0,
          filterReplacementStatus,
          filterStatus,
          replacementTime,
          filterHealthPercentage: null,
        });
      }
    };

    processData();
  }, [
    eventId,
    airQualityReading,
    externalAirQuality,
    hvacEvent?.timestamp,
    hvacSensor,
    hvacSensorAlerts,
    selectedHubId,
    // Removed getFilterOrderStatus from dependencies since we're handling it with polling
    hvacFilterStatus,
    t, // Added translation function to dependency array
  ]);

  return airQualityData;
};
